// Libs
import { Document, Image, Page, Text, View, Link } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
// Others
import {
  formatDate,
  getFullName,
  convertTimeToHHmm,
  formatCurrencyUSD,
  formatPhoneNumber,
  getParseHtmlText,
} from '~/utils/helper/common';
import { IPickSlipPdf } from '~/utils/interface/jobs';
import { DateFormatEnum, TypeListInventory } from '~/utils/enum/common';
import { EMPTY_STRING, PDF_POSITION_PLACEHOLDER_TO_SIGNING } from '~/utils/constants/common';
// Styles, images, icons
import { styles } from './styles';
import { images } from '~/assets';

type Props = {
  dataPdf: IPickSlipPdf;
};

const PickSlipPdf = (props: Props) => {
  //#region Destructuring Props
  const { dataPdf } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/order']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  //#endregion Handle Function

  return (
    <Document>
      <Page style={styles.page}>
        <View style={styles.mainContainer}>
          <View style={styles.header}>
            <View style={styles.viewOneHeader}>
              <Image style={styles.logo} src={images.commonLogo} />

              <View style={styles.viewRightOneHeader}>
                <Text style={[styles.titleTitle, styles.viewLeft]}>{t('order_pdf_sub_title_pick_slip')}</Text>

                <Text style={[styles.textLabel, styles.viewLeft]}>{t('order_pdf_sub_title')}</Text>

                <Text style={[styles.textContent, styles.viewLeft]}>{t('order_pdf_address')}</Text>
              </View>
            </View>

            <View style={styles.viewLine} />

            <View style={styles.viewTwoHeader}>
              <View style={styles.viewLeftTwoHeader}>
                <Text style={styles.textSubTitle}>{t('order_pdf_client')}</Text>
                <Text style={styles.textLabel}>
                  {getFullName({ firstName: dataPdf.client.firstName, lastName: dataPdf.client.lastName })}
                </Text>
                <View style={styled.height(6)} />
                <Text style={styles.textContent}>
                  {dataPdf?.client?.clientContacts && dataPdf?.client?.clientContacts[0]?.phoneNumber
                    ? formatPhoneNumber(dataPdf?.client?.clientContacts[0]?.phoneNumber)
                    : EMPTY_STRING}
                </Text>
                <Text style={styles.textContent}>
                  {dataPdf?.client?.clientContacts && dataPdf?.client?.clientContacts[0]?.email
                    ? dataPdf?.client?.clientContacts[0]?.email
                    : EMPTY_STRING}
                </Text>
              </View>

              <View style={styles.viewRightTwoHeader}>
                <View style={styles.viewItemHeaderTwo}>
                  <Text style={[styles.textLabel, styles.viewLabelTwo]}>{t('order_pdf_order_id')}</Text>
                  <Text style={styles.textContent}>{`#${dataPdf?.orderId}`}</Text>
                </View>

                <View style={styles.viewItemHeaderTwo}>
                  <Text style={[styles.textLabel, styles.viewLabelTwo]}>{t('order_pdf_event_start')}</Text>
                  <Text style={styles.textContent}>
                    {`${formatDate(dataPdf?.startDate, DateFormatEnum.MM_DD_YYYY)} - ${convertTimeToHHmm(
                      dataPdf?.startTime
                    )}`}
                  </Text>
                </View>

                <View style={styles.viewItemHeaderTwo}>
                  <Text style={[styles.textLabel, styles.viewLabelTwo]}>{t('order_pdf_event_end')}</Text>
                  <Text style={styles.textContent}>
                    {`${formatDate(dataPdf?.endDate, DateFormatEnum.MM_DD_YYYY)} - ${convertTimeToHHmm(
                      dataPdf?.endTime
                    )}`}
                  </Text>
                </View>
              </View>
            </View>
          </View>

          <View style={styles.tableContainer}>
            <Text style={styles.textSubTitle}>{t('order_pdf_product')}</Text>

            <View style={styles.tableHeader}>
              <Text style={styles.tableHeaderCellsStatus}>{t('order_pdf_status')}</Text>
              <Text style={styles.tableHeaderCells}>{t('order_pdf_name')}</Text>
              <Text style={styles.tableHeaderCellType}>{t('order_pdf_type')}</Text>
              <Text style={styles.tableHeaderCellType}>{t('order_pdf_quantity')}</Text>
              <Text style={styles.tableHeaderCell}>{t('order_pdf_price')}</Text>
              <Text style={styles.tableHeaderCell}>{t('order_pdf_total')}</Text>
            </View>
            {dataPdf?.jobItems &&
              dataPdf?.jobItems.length > 0 &&
              dataPdf?.jobItems?.map((item) => (
                <>
                  {!item.isShowOnPrint && (
                    <View key={item.id} style={styles.tableRow}>
                      <View style={styles.tableCellStatus}>
                        <View style={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}>
                          <View
                            style={{
                              width: 14,
                              height: 14,
                              borderWidth: 1,
                              borderColor: '#000',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {item?.isLoaded && <Text style={{ fontSize: 10, color: '#000' }}>X</Text>}
                          </View>
                          <Text style={{ fontSize: 8 }}>Loaded</Text>
                        </View>

                        <View style={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}>
                          <View
                            style={{
                              width: 14,
                              height: 14,
                              borderWidth: 1,
                              borderColor: '#000',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {item?.isDelivery && <Text style={{ fontSize: 10, color: '#000' }}>X</Text>}
                          </View>
                          <Text style={{ fontSize: 8 }}>Delivery</Text>
                        </View>

                        <View style={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}>
                          <View
                            style={{
                              width: 14,
                              height: 14,
                              borderWidth: 1,
                              borderColor: '#000',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {item?.isPickup && <Text style={{ fontSize: 10, color: '#000' }}>X</Text>}
                          </View>
                          <Text style={{ fontSize: 8 }}>Pickup</Text>
                        </View>
                      </View>
                      <View style={styles.tableCellName}>
                        {(() => {
                          return item?.url ? (
                            <Link src={item?.url} style={styles.textLink}>
                              <Text>{item?.name}</Text>
                            </Link>
                          ) : (
                            <Text>{item?.name}</Text>
                          );
                        })()}
                      </View>
                      <Text style={styles.tableCellType}>
                        {item?.type === TypeListInventory.PACKAGE
                          ? t('form_orders_label_package')
                          : t('order_pdf_product')}
                      </Text>
                      <Text style={styles.tableCellType}>{item?.quantity}</Text>
                      <Text style={styles.tableCell}>
                        {formatCurrencyUSD({ value: item?.price, showDecimal: true })}
                      </Text>
                      <Text style={styles.tableCell}>
                        {formatCurrencyUSD({ value: item?.total, showDecimal: true })}
                      </Text>
                    </View>
                  )}
                  {item?.jobAddOns &&
                    item?.jobAddOns.length > 0 &&
                    item?.jobAddOns
                      ?.filter((item) => !item.isShowOnPrint)
                      ?.map((subItem) => (
                        <View key={subItem.id} style={styles.tableRow}>
                          <View style={styles.tableCellStatus}>
                            <View style={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}>
                              <View
                                style={{
                                  width: 14,
                                  height: 14,
                                  borderWidth: 1,
                                  borderColor: '#000',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {subItem?.isLoaded && <Text style={{ fontSize: 10, color: '#000' }}>X</Text>}
                              </View>
                              <Text style={{ fontSize: 8 }}>Loaded</Text>
                            </View>

                            <View style={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}>
                              <View
                                style={{
                                  width: 14,
                                  height: 14,
                                  borderWidth: 1,
                                  borderColor: '#000',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {subItem?.isDelivery && <Text style={{ fontSize: 10, color: '#000' }}>X</Text>}
                              </View>
                              <Text style={{ fontSize: 8 }}>Delivery</Text>
                            </View>

                            <View style={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}>
                              <View
                                style={{
                                  width: 14,
                                  height: 14,
                                  borderWidth: 1,
                                  borderColor: '#000',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {subItem?.isPickup && <Text style={{ fontSize: 10, color: '#000' }}>X</Text>}
                              </View>
                              <Text style={{ fontSize: 8 }}>Pickup</Text>
                            </View>
                          </View>
                          <View style={styles.tableCellName}>
                            {(() => {
                              return subItem?.url ? (
                                <Link src={subItem?.url} style={styles.textLink}>
                                  <Text>{subItem?.name}</Text>
                                </Link>
                              ) : (
                                <Text>{subItem?.name}</Text>
                              );
                            })()}
                          </View>

                          <Text style={styles.tableCellType}>
                            {item.type === TypeListInventory.PACKAGE && subItem?.type === TypeListInventory.PRODUCT
                              ? t('order_detail_table_package_sub')
                              : t('order_detail_table_add_on_sub')}
                          </Text>
                          <Text style={styles.tableCellType}>{subItem?.quantity}</Text>
                          <Text style={styles.tableCell}>
                            {formatCurrencyUSD({ value: subItem?.price, showDecimal: true })}
                          </Text>
                          <Text style={styles.tableCell}>
                            {formatCurrencyUSD({ value: subItem?.total, showDecimal: true })}
                          </Text>
                        </View>
                      ))}
                </>
              ))}
          </View>

          <View style={styles.viewNoteContent}>
            <Text style={styles.viewAdjustmentLabel}>{t('order_pdf_external_note')}</Text>
            <Text>{dataPdf?.externalNote}</Text>
          </View>

          <View style={styles.viewTotalContainer}>
            <View style={styles.viewAdjustment}></View>

            <View style={styles.viewTotalContent}>
              {/* <Text style={styles.textSubTitle}>{t('order_pdf_total')}</Text> */}
              <View style={styles.viewAdjustmentContainer}>
                <View style={styles.viewAdjustmentItem}>
                  <Text style={styles.viewAdjustmentLabel}>{t('order_pdf_total_title')}</Text>
                  <Text style={styles.viewAdjustmentLabel}>
                    {formatCurrencyUSD({ value: dataPdf?.total, showDecimal: true })}
                  </Text>
                </View>
              </View>
            </View>
          </View>

          <View style={styles.viewSignature}>
            <View style={styles.viewSignatureClient}>
              <Text style={styles.textSubTitle}>{t('order_pdf_signature')}</Text>
              <Text style={{ fontSize: 40, opacity: 0 }}>{PDF_POSITION_PLACEHOLDER_TO_SIGNING}</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

const styled = {
  height: (height: number) => ({
    height: height,
  }),
};

export default PickSlipPdf;
